<template>
  <div>
    <div v-if="show_spinner" class="flex justify-center">
      <pl-spinner />
    </div>
    <div v-else class="grid grid-cols-1 xl:grid-cols-2 gap-x-2.5 gap-y-2.5">
      <!-- Participation Rate Card -->
      <div class="col-span-1 flex flex-col rounded border border-solid border-gray-200">
        <div class="mb-5 py-3 border-b border-solid border-gray-200 px-4 flex justify-between items-center">
          <span class="font-semibold text-base text-blueGray-800 font-poppins">Participation Rate</span>
          <div class="flex items-center">
            <button class="mr-2 text-gray-700 hover:text-gray-800">
              <i class="fas fa-info-circle" v-popover:participation-rate.left />
            </button>
          </div>
        </div>
        <popover class="mr-10 p-0 bg-white drop-shadow absolute z-auto" name="participation-rate" :width="420">
          <div class="px-6 py-4 font-semibold border-b">Information</div>
          <div class="px-6 py-4">
            Students have “participated” when they have at least one turn as
            speaker during the RoundTable.
          </div>
        </popover>
        <div v-if="participation_rate_data" class="relative">
          <Chart
            type="pie"
            :data="participation_rate_data"
            :options="participation_rate_options"
          />
          <div class="mt-4 flex justify-center">
            <span class="font-bold" :style="{ color: color_rate }">
              {{ rate }}%
            </span>
            <span class="ml-1 text-gray-500">participated in the discussion</span>
          </div>
        </div>
        <div class="mt-auto mb-auto" v-else>
          <span class="flex justify-center items-center">
            No Data to Display
          </span>
        </div>
      </div>
      <!-- Type of Contributions Card -->
      <div class="col-span-1 h-full flex flex-col rounded border border-solid border-gray-200">
        <div class="mb-10">
          <div class="mb-5 py-3 px-4 flex justify-between items-center border-b border-solid border-gray-200">
            <span class="font-semibold text-base text-blueGray-800 font-poppins">Types of Contributions</span>
            <div class="flex items-center">
              <button class="mr-2 text-gray-700 hover:text-gray-800">
                <i class="fas fa-info-circle" v-popover:contribution-info.left></i>
              </button>
              <button class="text-gray-700 hover:text-gray-800">
                <i class="fas fa-question-circle" v-popover:contribution-questions.left></i>
              </button>
            </div>
          </div>
          <popover
            class="mr-10 p-0 bg-white drop-shadow absolute z-auto"
            name="contribution-info"
            :width="600"
          >
            <div class="px-6 py-4 font-semibold border-b">Information</div>
            <div class="px-6 py-4">
              This chart counts the number of times a student was assigned as
              speaker for each tap-in type.
            </div>
          </popover>
          <popover
            class="mr-10 p-0 bg-white drop-shadow absolute z-20"
            name="contribution-questions"
            :width="650"
          >
            <div class="px-4 py-4">
              <h5 class="font-semibold">Reflection Questions</h5>
            </div>
            <div class="border-b"></div>
            <div class="px-6 py-4" v-html="reflections.total_tap_ins" />
          </popover>
          <div class="relative" v-if="is_have_contributions">
            <Chart
              type="pie"
              :data="contribution_data"
              :options="contribution_options"
              :height="165"
            />
          </div>
          <div v-else class="flex h-full relative justify-center items-center">
            <div class="text-center py-28">
              <span>No data to display</span>
            </div>
          </div>
        </div>
      </div>
      <!-- Participation Summary -->
      <div class="col-span-1 flex flex-col rounded border border-solid border-gray-200">
        <div class="mb-5 py-3 px-4 border-b border-solid border-gray-200 flex justify-between items-center">
          <h6 class="font-semibold text-base text-blueGray-800 font-poppins">Participation Summary</h6>
          <div class="flex items-center">
            <button class="mr-2 text-gray-700 hover:text-gray-800">
              <i class="fas fa-info-circle" v-popover:time-spent-info.left />
            </button>
            <button class="text-gray-700 hover:text-gray-800">
              <i class="fas fa-question-circle" v-popover:time-spent-question.left></i>
            </button>
          </div>
        </div>
        <div class="px-7 pt-2 pb-4">
          <div class="rounded-lg py-1 px-1 bg-trueGray-100 border border-solid border-gray-200" style="width: 120px;">
            <ul class="flex items-center space-x-1">
              <li>
                <div 
                  @click="handleChangeTabParticipationSummary('time-spent')" 
                  :class="selectedTabParticipationSummary === 'time-spent' ? 'bg-blueGray-700 text-blueGray-100' : 'text-blueGray-700'" 
                  class="flex cursor-pointer items-center space-x-1.5 px-2 rounded-md py-1"
                >
                  <span>Time</span>
                </div>
              </li>
              <li>
                <div 
                  @click="handleChangeTabParticipationSummary('tap-in')" 
                  :class="selectedTabParticipationSummary === 'tap-in' ? 'bg-blueGray-700 text-blueGray-100' : 'text-blueGray-700'" 
                  class="flex cursor-pointer items-center space-x-1.5 px-2 rounded-md py-1"
                >
                  <span>Tap In</span>
                </div>
              </li>
            </ul>
          </div>
          <div v-if="selectedTabParticipationSummary === 'time-spent'" class="relative mt-5">
            <TimeSpentBar @showLabelNoData="(value) => (showLabelNoDataTimeBar = value)" />
          </div>
          <div v-else>
            <div v-if="showLabelNoDataTapIn === false" class="flex w-full justify-center items-center h-8">
              <TapInCounter />
              <span class="ml-1 text-gray-500">
                Average speaking turns per student
              </span>
            </div>
            <div class="relative">
              <TapInSummaryBar @showLabelNoData="(value) => (showLabelNoDataTapIn = value)" />
            </div>
            <div v-if="showLabelNoDataTapIn === true || showLabelNoDataTimeBar === true">
              <div class="text-center py-28">
                <span>No data to display</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Chord Diagram Verbal -->
      <div class="col-span-1 flex flex-col h-full rounded border border-solid border-gray-200">
        <div class="relative h-full">
          <div class="mb-5 flex justify-between items-center py-3 px-4 border-b border-solid border-gray-200">
            <span class="font-semibold text-base text-blueGray-800 font-poppins">Chord Diagram</span>
            <div class="flex items-center">
              <button class="mr-2 text-gray-700 hover:text-gray-800">
                <i class="fas fa-info-circle" v-popover:contribution-info.left></i>
              </button>
              <button class="text-gray-700 hover:text-gray-800">
                <i class="fas fa-question-circle" v-popover:contribution-questions.left></i>
              </button>
            </div>
          </div>
          <div class="flex items-center justify-center h-full -mt-12 py-3 px-6">
            <ChordDiagramverbal />
          </div>
        </div>
      </div>
      <div class="col-span-1 flex flex-col h-full rounded border border-solid border-gray-200">
        <div class="relative h-full">
          <div class="mb-5 flex justify-between items-center border-b border-solid border-gray-200 py-2 px-4">
            <span class="font-semibold text-base text-blueGray-800 font-poppins">Word Cloud</span>
            <div class="flex items-center space-x-1">
              <button @click="handleRefreshWordCloud()" title="Refresh"class="bg-white border border-solid border-gray-200 text-sm hover:bg-gray-100 rounded px-3 py-1">
                <i v-if="isRefreshingCloud === true" class="animate-spin fa-solid fa-arrows-rotate"></i>
                <i v-if="isRefreshingCloud === false" class="fa-solid fa-arrows-rotate"></i>
              </button>
              <button title="Change Word Cloud" @click="handleOpenEditWordCloud()" class="bg-white border border-solid border-gray-200 text-sm hover:bg-gray-100 rounded px-3 py-1 flex items-center space-x-1.5">
                <i class="fa-solid fa-pen-to-square"></i>
                <span>Edit Words</span>
              </button>
            </div>
          </div>
          <div v-if="isWordCloudLoading === false" class="flex items-center justify-center h-full px-5 py-5 -mt-12">
            <VueWordCloud
              style="width: 100%; height: 500px; margin: 0"
              :spacing="0.2"
              :words="wordCloudData"
              :color="([, , sent]) => sent === 'blu' ? '#72CCD7' : sent === 'red' ? '#EB2D7C' : '#273248'"
              font-family="Poppins"
              font-weight="Bold"
            >
              <template slot-scope="{ text, weight }">
                <div :title="weight" class="cursor-pointer">
                  {{ text }}
                </div>
              </template>
            </VueWordCloud>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import { mapGetters } from 'vuex';
  import liveDiscussionService from 'services/liveDiscussion';
  import horizontalStackedBar from 'components/dataVisualization/d3js/horizontalStackedBar';
  import participationCounter from 'components/dataVisualization/d3js/participationCounter';
  import reflections from '../../measureLiveDiscussion/reflectionQuestions';
  import ChordDiagramverbal from 'components/dataVisualization/d3js/ChordDiagramverbal.vue';
  import VueWordCloud from 'vuewordcloud';
  import Axios from 'axios';
  import { EventBus } from 'utils/event-bus';

  export default {
    components: {
      VueWordCloud,
      horizontalStackedBar,
      TapInCounter: () =>
        import('components/dataVisualization/d3js/tapInCounter'),
      participationCounter,
      TimeSpentBar: () =>
        import('../../measureLiveDiscussion/graphs/timeSpentBar.vue'),
      TapInSummaryBar: () =>
        import('../../measureLiveDiscussion/graphs/tapInSummaryBar.vue'),
      Chart: () => import('components/chartJS/chartJS'),
      ChordDiagramverbal
    },
    data() {
      return {
        show_spinner: false,
        rate: 0,
        color_rate: '',
        participation_rate_data: {},
        participation_rate_options: {},
        contribution_data: {},
        contribution_options: {},
        tap_in_summary_data: {},
        tap_in_summary_options: {},
        dataTimeSpentBar: null,
        showLabelNoDataTimeBar: undefined,
        showLabelNoDataTapIn: undefined,
        is_have_contributions: false,
        selectedTabParticipationSummary: 'time-spent',
        word_cloud_words: null,
        keywordData: null,
        wordCloudLanguage: '',
        wordCloudData: [],
        isWordCloudLoading: false,
        isRefreshingCloud: false,
        refreshedWords: false
      };
    },
    computed: {
      ...mapGetters([
        'getCurrentDiscussion',
        'hasTeacherPermissionsInCourse',
        'getClassSummaryLD',
      ]),
      reflections() {
        return reflections;
      },
    },
    watch: {
      'getCurrentDiscussion.keywords': function() {
        this.wordCloudData = [];
        this.handleGetDiscussionWord();
      }
    },
    methods: {
      handleRefreshWordCloud: async function() {
        this.isRefreshingCloud = true;
        try {
          this.wordCloudData = [];
          const currentKeywordState = { ...this.keywordData };
          this.keywordData = [];

          const words = await Axios.get(`/api/transcriptions/${this.getCurrentDiscussion.id}/words`);
          this.keywordData = words.data.keywords;
          this.wordCloudLanguage = words.data.language;

          const keys = Object.keys(this.keywordData);
          const len = Math.min(keys.length, 80);

          for (let i = 0; i < len; i++) {
            const keyword = keys[i];
            const keywordInfo = this.keywordData[keyword];

            if (currentKeywordState[keyword] && currentKeywordState[keyword]['removed']) {
              this.keywordData[keyword]['removed'] = true;
            } else {
              this.keywordData[keyword]['removed'] = this.keywordData[keyword]['removed'] === 'False';
            }

            if (!this.keywordData[keyword]['removed']) {
              const sentimentColor = keywordInfo.overall_sentiment === 'positive' ? 'blu' : keywordInfo.overall_sentiment === 'neutral' ? 'dark' : 'red';

              this.wordCloudData.push([
                keyword,
                keywordInfo.sentences.length,
                sentimentColor,
              ]);
            }
          }

          await this.$store.dispatch('saveLiveDiscussionSettings', {
            id: this.getCurrentDiscussion.id,
            keywords: JSON.stringify(this.keywordData)
          });

          this.refreshedWords = true;

          this.wordCloudData = this.wordCloudData;

          this.isRefreshingCloud = false;

        } catch (error) {
            console.log(error);
            this.isRefreshingCloud = false;
        }
      },
      handleOpenEditWordCloud: function() {
        if (this.keywordData) {
          EventBus.$emit('handleOpenEditWordCloudVR', this.keywordData);
        }
        
      },
      initializeWordCloud() {
        
      },
      handleChangeTabParticipationSummary: async function(tab) {
        this.selectedTabParticipationSummary = tab;
        return;
      },
      fetchClassSummary() {
        this.show_spinner = true;
        const live_id = this.$route.params.liveDiscussionID;
        this.$store.dispatch(
          'analytics',
          'teacher - access verbal discussion metrics'
        );
        return liveDiscussionService
          .classSummaryLD(live_id)
          .then(async (response) => {
            await this.$store.dispatch('postClassSummaryLD', response.data);
            this.show_spinner = false;
          })
          .catch((err) => {
            console.error(err);
            this.show_spinner = false;
          });
      },
      participationRate() {
        let participations = 0;
        this.getClassSummaryLD.student_participations.forEach((s) => {
          s.times_spoken > 0 ? participations++ : null;
        });
        let endVal = Math.floor(
          (participations /
            this.getClassSummaryLD.student_participations.length) *
            100
        );
        this.rate = isNaN(endVal) ? 0 : endVal;

        this.color_rate =
          this.rate < 35
            ? 'red'
            : this.rate > 36 && this.rate < 69
            ? '#F5BE30'
            : '#30A92E';

        this.participation_rate_data = {
          labels: [],
          datasets: [
            {
              label: 'My First Dataset',
              data: [100 - this.rate, this.rate],
              backgroundColor: ['#E0E0E0', this.color_rate],
            },
          ],
        };
        this.participation_rate_options = {
          tooltips: {
            callbacks: {
              label: (tooltipItem) => {
                let value = 0;
                if (tooltipItem.index === 0) {
                  value =
                    this.getClassSummaryLD.student_participations.length -
                    participations;
                  return ' Did not participated: ' + value;
                } else {
                  value = participations;
                  return ' Participated: ' + value;
                }
              },
            },
          },
        };
      },
      contributionType() {
        let total_new_ideas = 0;
        let total_build_ons = 0;
        let total_challenges = 0;
        let total_questions = 0;
        let total_hand_raise = 0;
        this.getClassSummaryLD.student_participations.forEach((s) => {
          total_new_ideas += s.turns_data.new_idea;
          total_build_ons += s.turns_data.build_on;
          total_challenges += s.turns_data.challenge;
          total_questions += s.turns_data.question;
          total_hand_raise += s.turns_data.hand_raise;
        });
        if (
          total_new_ideas > 0 ||
          total_build_ons > 0 ||
          total_challenges > 0 ||
          total_questions > 0 ||
          total_hand_raise > 0
        ) {
          this.is_have_contributions = true;
        }

        this.contribution_data = {
          labels: [
            'New Ideas',
            'Build Ons',
            'Challenges',
            'Questions',
            'Added By Teacher',
          ],
          datasets: [
            {
              label: 'My First Dataset',
              data: [
                total_new_ideas,
                total_build_ons,
                total_challenges,
                total_questions,
                total_hand_raise,
              ],
              backgroundColor: [
                '#72CCD7',
                '#157F1F',
                '#EB2D7C',
                '#00008B',
                '#FFA500',
              ],
            },
          ],
        };
        this.contribution_options = {
          legend: {
            enabled: true,
            position: 'bottom',
            onHover: function (e) {
              e.target.style.cursor = 'pointer';
            },
            labels: {
              boxWidth: 15,
              padding: 20,
            },
          },
        };
      },
      handleGetDiscussionWord: async function() {
        this.isWordCloudLoading = true;
        if (this.getCurrentDiscussion.keywords === null) {
          try {
            const words = await Axios.get(`/api/transcriptions/${this.getCurrentDiscussion.id}/words`);
            this.keywordData = words.data.keywords;
            this.wordCloudLanguage = words.data.language;

            const keys = Object.keys(this.keywordData);
            const len = Math.min(keys.length, 80);

            for (let i = 0; i < len; i++) {
              const keyword = keys[i];
              const keywordInfo = this.keywordData[keyword];
              const sentimentColor = keywordInfo.overall_sentiment === 'positive' ? 'blu' : keywordInfo.overall_sentiment === 'neutral' ? 'dark' : 'red';
              this.wordCloudData.push([
                keyword,
                keywordInfo.sentences.length,
                sentimentColor,
              ]);
            }

            for (let i = 0; i < len; i++) {
              const word = Object.keys(this.keywordData)[i];
              if (word !== 'l') {
                this.keywordData[word]['removed'] = this.keywordData[word]['removed'] === 'False';
              }
            }

            await this.$store.dispatch('saveLiveDiscussionSettings', { id: this.getCurrentDiscussion.id, keywords: JSON.stringify(this.keywordData)});

          } catch (error) { 
            console.log(error);
          }
        } else {
          this.keywordData = JSON.parse(this.getCurrentDiscussion.keywords);
          this.wordCloudLanguage = "en";

          const keys = Object.keys(this.keywordData);
          const len = Math.min(keys.length, 80);

          for (let i = 0; i < len; i++) {
            const keyword = keys[i];
            const keywordInfo = this.keywordData[keyword];

            if (keywordInfo.removed === false) {
              const sentimentColor = keywordInfo.overall_sentiment === 'positive' ? 'blu' : keywordInfo.overall_sentiment === 'neutral' ? 'dark' : 'red';
              this.wordCloudData.push([
                keyword,
                keywordInfo.sentences.length,
                sentimentColor,
              ]);
            }
            
          }
        }
        this.isWordCloudLoading = false;
      }
    },
    async mounted() {
      await this.fetchClassSummary();
      await this.participationRate();
      await this.contributionType();
      await this.handleGetDiscussionWord();
    },
  };
</script>



// WEBPACK FOOTER //
// LDSummaryTab.vue?524ae0c6